import { config } from './config';

/**
 * GA4 funnel events. Loaded only after consent (see lib/consent and main.tsx).
 * Event names follow GA4 ecommerce recommendations so reports work out of the box.
 */
let loaded = false;

function disableFlag(): string {
  return `ga-disable-${config.ga4Id}`;
}

function clearGaCookies(): void {
  const host = location.hostname.split('.');
  const domains = [
    location.hostname,
    ...host.slice(1).map((_, i) => `.${host.slice(i + 1).join('.')}`),
  ];
  for (const c of document.cookie.split(';')) {
    const name = c.split('=')[0]?.trim();
    if (!name || !(name === '_ga' || name.startsWith('_ga_') || name === '_gid')) continue;
    for (const d of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${d}`;
    }
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}

/** Revocation: stop sending hits (gtag honours ga-disable-<id>) and drop the GA cookies. */
export function stopAnalytics(): void {
  if (!config.ga4Id) return;
  (window as unknown as Record<string, unknown>)[disableFlag()] = true;
  window.gtag = undefined;
  clearGaCookies();
}

/** Call only with explicit consent: this is the first request to googletagmanager.com. */
export function loadAnalytics(): void {
  if (!config.ga4Id) return;
  (window as unknown as Record<string, unknown>)[disableFlag()] = false;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  // Re-granted in the same page view: the script is already there, only gtag was dropped.
  if (loaded) return;
  loaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${config.ga4Id}`;
  document.head.appendChild(s);
  window.gtag('js', new Date());
  window.gtag('config', config.ga4Id, { anonymize_ip: true, send_page_view: false });
}

export function track(event: string, params: Record<string, unknown> = {}): void {
  window.gtag?.('event', event, params);
}

export const funnel = {
  viewFormats: () => track('view_item_list', { item_list_id: 'formats' }),
  selectFormat: (id: string, name: string, priceCents: number) =>
    track('select_item', { items: [{ item_id: id, item_name: name, price: priceCents / 100 }] }),
  beginCheckout: (valueCents: number) =>
    track('begin_checkout', { currency: 'EUR', value: valueCents / 100 }),
  purchase: (orderId: string, valueCents: number) =>
    track('purchase', { transaction_id: orderId, currency: 'EUR', value: valueCents / 100 }),
  inviteSent: (count: number) => track('share', { method: 'email', content_type: 'invite', count }),
  loginRequested: () => track('login', { method: 'magic_link' }),
};
